import React from "react";
import { endOfDay, startOfDay } from "date-fns";
import { ListFilter } from "lucide-react";
import { DateRangePicker } from "@/components/common/DateRangePicker";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import type { DateRange } from "@/hooks/useDateRange";
import type { TenantSubscriptionTransactionStatus } from "@/types/tenant-subscription";
import { useTranslation } from "react-i18next";

const FILTER_CONTROL_CLASS =
  "w-full min-w-0 max-w-full justify-between gap-2 !min-h-8 !h-8 !px-4 !py-1 !border !border-slate-200 text-foreground !text-sm !font-medium shadow-xs overflow-hidden [&_[data-slot=select-value]]:block [&_[data-slot=select-value]]:min-w-0 [&_[data-slot=select-value]]:max-w-full [&_[data-slot=select-value]]:truncate";

interface SubscriptionPaymentsFilterProps {
  hotelName: string;
  planTitle: string;
  status: TenantSubscriptionTransactionStatus | "all";
  createdFrom: string;
  createdTo: string;
  statusOptions: TenantSubscriptionTransactionStatus[];
  onHotelNameChange: (value: string) => void;
  onPlanTitleChange: (value: string) => void;
  onStatusChange: (value: TenantSubscriptionTransactionStatus | "all") => void;
  onCreatedFromChange: (value: string) => void;
  onCreatedToChange: (value: string) => void;
}

export const SubscriptionPaymentsFilter: React.FC<
  SubscriptionPaymentsFilterProps
> = ({
  hotelName,
  planTitle,
  status,
  createdFrom,
  createdTo,
  statusOptions,
  onHotelNameChange,
  onPlanTitleChange,
  onStatusChange,
  onCreatedFromChange,
  onCreatedToChange,
}) => {
  const { t } = useTranslation();

  const toDateOrUndefined = (value: string) => {
    if (!value) return undefined;

    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? undefined : date;
  };

  const dateRange: DateRange = {
    from: toDateOrUndefined(createdFrom),
    to: toDateOrUndefined(createdTo),
  };

  const handleDateRangeChange = (range: DateRange) => {
    onCreatedFromChange(range.from ? startOfDay(range.from).toISOString() : "");
    onCreatedToChange(range.to ? endOfDay(range.to).toISOString() : "");
  };

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          variant="icon-text"
          className="min-h-8 h-8 gap-2 border border-slate-200 py-1 text-sm font-medium text-foreground shadow-xs"
        >
          <ListFilter className="size-4" />
          {t("common.filter")}
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="w-[380px] max-w-[calc(100vw-2rem)] p-3"
        align="start"
      >
        <div className="grid gap-2">
          <Input
            value={hotelName}
            placeholder={t("billing.subscriptionPayments.filters.hotelName")}
            onChange={(e) => onHotelNameChange(e.target.value)}
            className="!h-8 !min-h-8 !px-4 !py-1 border-slate-200 text-foreground !text-sm !font-medium shadow-xs"
          />
          <Input
            value={planTitle}
            placeholder={t("billing.subscriptionPayments.filters.planTitle")}
            onChange={(e) => onPlanTitleChange(e.target.value)}
            className="!h-8 !min-h-8 !px-4 !py-1 border-slate-200 text-foreground !text-sm !font-medium shadow-xs"
          />
          <div className="flex flex-col gap-1.5">
            <Select
              value={status}
              onValueChange={(value) =>
                onStatusChange(
                  value as TenantSubscriptionTransactionStatus | "all",
                )
              }
            >
              <SelectTrigger className={FILTER_CONTROL_CLASS}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">
                  {t("billing.subscriptionPayments.filters.allStatuses")}
                </SelectItem>
                {statusOptions.map((option) => (
                  <SelectItem key={option} value={option}>
                    {option}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <DateRangePicker
            dateRange={dateRange}
            onDateRangeChange={handleDateRangeChange}
            className={FILTER_CONTROL_CLASS}
          />
        </div>
      </PopoverContent>
    </Popover>
  );
};
